// Reduction compute cache + lightweight event bus.
//
// Compute results derive from the spec + the current field array; they are
// not independent state, so they don't belong in the params store. Keeping
// the cache in this module lets the UI subscribe via `onResult(id, fn)`
// without round-tripping store mutations for every recompute.
//
// `clear()` is called when a Reduction is deleted or the dataset switches.

import type { ReductionId } from '@store/schema';
import type { ReductionResult } from './types';

const results = new Map<ReductionId, ReductionResult>();
const subscribers = new Map<ReductionId, Set<(r: ReductionResult) => void>>();
const allSubscribers = new Set<(id: ReductionId, r: ReductionResult) => void>();

export function setResult(id: ReductionId, r: ReductionResult): void {
    results.set(id, r);
    const fns = subscribers.get(id);
    if (fns) {
        for (const fn of fns) {
            try { fn(r); } catch (e) { console.error('[reductions/cache] subscriber error', e); }
        }
    }
    for (const fn of allSubscribers) {
        try { fn(id, r); } catch (e) { console.error('[reductions/cache] subscriber error', e); }
    }
}

export function getResult(id: ReductionId): ReductionResult | undefined {
    return results.get(id);
}

export function clearResult(id: ReductionId): void {
    results.delete(id);
    const fns = subscribers.get(id);
    if (fns) fns.clear();
}

export function clearAll(): void {
    results.clear();
    subscribers.clear();
    allSubscribers.clear();
}

/** Subscribe to recomputes for a specific id. Returns disposer. */
export function onResult(id: ReductionId, fn: (r: ReductionResult) => void): () => void {
    let s = subscribers.get(id);
    if (!s) { s = new Set(); subscribers.set(id, s); }
    s.add(fn);
    return () => {
        s!.delete(fn);
        if (s!.size === 0) subscribers.delete(id);
    };
}

/** Subscribe to recomputes for any id. Returns disposer. */
export function onAnyResult(fn: (id: ReductionId, r: ReductionResult) => void): () => void {
    allSubscribers.add(fn);
    return () => { allSubscribers.delete(fn); };
}
